import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DatabaseStatus } from './DatabaseStatus';
import { AuthSplash } from './AuthShell';

interface SetupGuardProps {
  children: React.ReactNode;
}

export const SetupGuard: React.FC<SetupGuardProps> = ({ children }) => {
  const { requiresSetup, setupLoading, isLoading } = useAuth();
  const navigate = useNavigate();
  const [showDatabaseStatus, setShowDatabaseStatus] = React.useState(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = React.useState(false);

  // Check if user has seen onboarding and listen for changes
  useEffect(() => {
    const updateOnboardingStatus = () => {
      const seenOnboarding = localStorage.getItem('hasSeenOnboarding');
      setHasSeenOnboarding(seenOnboarding === 'true');
    };
    
    // Initial check
    updateOnboardingStatus();
    
    // Listen for storage changes (when localStorage is updated from another tab/window)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'hasSeenOnboarding') {
        updateOnboardingStatus();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Redirect to onboarding or setup if required
  useEffect(() => {
    console.log('SetupGuard effect:', { 
      setupLoading, 
      isLoading, 
      requiresSetup, 
      hasSeenOnboarding,
      currentPath: window.location.pathname
    });
    
    if (!setupLoading && !isLoading && requiresSetup) {
      const currentPath = window.location.pathname;
      
      // Check localStorage directly for the most up-to-date value
      const seenOnboarding = localStorage.getItem('hasSeenOnboarding') === 'true';
      
      if (!seenOnboarding && currentPath !== '/onboarding') {
        console.log('Redirecting to onboarding');
        navigate('/onboarding', { replace: true });
      } else if (seenOnboarding && currentPath !== '/setup') {
        console.log('Redirecting to setup');
        navigate('/setup', { replace: true });
      }
    }
  }, [requiresSetup, setupLoading, isLoading, hasSeenOnboarding, navigate]);

  // Show loading while checking setup status
  if (setupLoading || isLoading) {
    return <AuthSplash label="Checking system status" />;
  }

  // Show database status if needed
  if (showDatabaseStatus) {
    return (
      <DatabaseStatus
        onReady={() => setShowDatabaseStatus(false)}
        onSetupRequired={() => navigate('/setup')}
      />
    );
  }

  // Otherwise, render the normal app
  return <>{children}</>;
};
